import React from 'react';

/**
 * Renders a UTC timestamp as local time with the original UTC time shown in a tooltip
 * @param {string} utcTimestamp - ISO 8601 UTC timestamp string
 * @param {Object} options - Optional formatting options
 * @param {boolean} options.showDate - Whether to include date (default: true)
 * @param {boolean} options.showTime - Whether to include time (default: true)
 * @param {boolean} options.showSeconds - Whether to include seconds (default: false)
 * @returns {JSX.Element} - Span element with local time and UTC tooltip
 */
export const renderLocalTimeWithUtcTooltip = (utcTimestamp, options = {}) => {
  const {
    showDate = true,
    showTime = true,
    showSeconds = false
  } = options;

  // Return N/A if no timestamp provided
  if (!utcTimestamp || utcTimestamp === 'N/A') {
    return <span>N/A</span>;
  }

  try {
    // Parse the UTC timestamp
    const utcDate = new Date(utcTimestamp);
    
    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      return <span>Invalid Date</span>;
    }

    // Format local time based on options
    let localTimeString = '';
    
    if (showDate && showTime) {
      localTimeString = utcDate.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        ...(showSeconds && { second: '2-digit' })
      });
    } else if (showDate) {
      localTimeString = utcDate.toLocaleDateString();
    } else if (showTime) {
      localTimeString = utcDate.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        ...(showSeconds && { second: '2-digit' })
      });
    }

    // Format UTC time for tooltip
    const utcTimeString = utcDate.toISOString();
    const formattedUtc = `${utcTimeString.replace('T', ' ').replace('Z', ' UTC')}`;

    return (
      <span 
        title={`UTC: ${formattedUtc}`}
        style={{ 
          cursor: 'help',
          textDecoration: 'underline dotted',
          textUnderlineOffset: '2px'
        }}
      >
        {localTimeString}
      </span>
    );
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return <span>Invalid Date</span>;
  }
};

/**
 * Convenience function for showing just the date portion
 * @param {string} utcTimestamp - ISO 8601 UTC timestamp string
 * @returns {JSX.Element} - Span element with local date and UTC tooltip
 */
export const renderLocalDateWithUtcTooltip = (utcTimestamp) => {
  return renderLocalTimeWithUtcTooltip(utcTimestamp, { showTime: false });
};

/**
 * Convenience function for showing just the time portion
 * @param {string} utcTimestamp - ISO 8601 UTC timestamp string
 * @param {boolean} showSeconds - Whether to include seconds (default: false)
 * @returns {JSX.Element} - Span element with local time and UTC tooltip
 */
export const renderLocalTimeOnlyWithUtcTooltip = (utcTimestamp, showSeconds = false) => {
  return renderLocalTimeWithUtcTooltip(utcTimestamp, { 
    showDate: false, 
    showTime: true, 
    showSeconds 
  });
}; 